import { Link } from "react-router-dom";
import type { ProjectDetail } from "@/mocks/admin/projects";

type CrewMemberItem = ProjectDetail["crew"][number];

type Props = {
  crew: ProjectDetail["crew"];
  truck: ProjectDetail["truck"];
  onMessageCrew?: (crew: CrewMemberItem) => void;
};

export default function CrewCard({ crew, truck, onMessageCrew }: Props) {
  return (
    <div className="bg-white border border-ink-100 rounded-lg">
      <div className="px-5 pt-5 pb-4 border-b border-ink-100 flex items-center justify-between">
        <div>
          <h3 className="font-display text-base font-bold text-ink-900">Crew & Truck</h3>
          <p className="text-[11px] text-ink-500 mt-0.5">
            {crew.length} crew assigned · {truck.size}
          </p>
        </div>
        <button className="text-xs font-semibold text-brand-600 hover:text-brand-700 cursor-pointer whitespace-nowrap">
          + Assign
        </button>
      </div>

      {/* Truck info */}
      <div className="px-5 py-4 bg-cream-50 border-b border-ink-100">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-md bg-ink-900 text-white flex items-center justify-center">
            <i className="ri-truck-line text-lg"></i>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-ink-900">{truck.size}</div>
            <div className="text-[11px] text-ink-500 font-mono">Plate · {truck.plate}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">Load</div>
            <div className="text-sm font-bold text-ink-900 tabular-nums">
              {Math.round((truck.needed / truck.capacityCuFt) * 100)}%
            </div>
          </div>
        </div>
        <div className="w-full h-1.5 rounded-full bg-ink-100 mt-3 overflow-hidden">
          <div
            className="h-full bg-brand-500 rounded-full"
            style={{ width: `${Math.min(100, (truck.needed / truck.capacityCuFt) * 100)}%` }}
          ></div>
        </div>
        <div className="flex items-center justify-between mt-1.5 text-[11px] text-ink-500 tabular-nums">
          <span>{truck.needed} cu ft needed</span>
          <span>{truck.capacityCuFt} cu ft capacity</span>
        </div>
      </div>

      <ul className="divide-y divide-ink-100">
        {crew.map((c) => (
          <li key={c.id}>
            <Link
              to={`/crew/${c.id}`}
              className="px-5 py-3 flex items-center gap-3 hover:bg-cream-50 transition-colors cursor-pointer group"
            >
              <div
                className={`w-10 h-10 rounded-full ${c.color} text-white text-xs font-bold flex items-center justify-center shrink-0`}
              >
                {c.initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-ink-900 truncate group-hover:text-brand-700 transition-colors">
                  {c.name}
                </div>
                <div className="text-[11px] text-ink-500 flex items-center gap-2 flex-wrap">
                  <span>{c.role}</span>
                  <span className="inline-flex items-center gap-0.5 text-amber-600">
                    <i className="ri-star-fill text-[10px]"></i>
                    {c.rating}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <a
                  href={`tel:${c.phone}`}
                  onClick={(e) => e.stopPropagation()}
                  title={`Call ${c.name}`}
                  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-ink-100 text-ink-500 cursor-pointer"
                >
                  <i className="ri-phone-line text-sm"></i>
                </a>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onMessageCrew?.(c);
                  }}
                  title={`Text ${c.name}`}
                  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-ink-100 text-ink-500 cursor-pointer"
                >
                  <i className="ri-message-2-line text-sm"></i>
                </button>
                <i className="ri-arrow-right-s-line text-ink-300 group-hover:text-ink-500 group-hover:translate-x-0.5 transition-all"></i>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}